import { defaultMaximumPagination, RequestConfig } from 'helpers';
import {
  createApiCall,
  DataWithPagination,
  paginatedApiCallZeroBased,
  showNotification,
} from 'services/api';
import { transformProject, transformToRequestProject } from './transformations';
import { Project } from './types';

export const postProject = async (data: Project): Promise<Project> => {
  const res = await createApiCall(
    {
      path: '/v1/projects',
      method: 'POST',
      body: transformToRequestProject(data),
    },
    {
      getMessage: {
        error: (error: any) => {
          const message =
            error.response?.data?.message || 'Failed to create project';

          if (message.toLowerCase().startsWith('duplicate')) {
            return 'A project with this name already exists for this customer.';
          }

          return message;
        },
      },
    }
  )();

  showNotification('Project successfully created', { variant: 'success' });
  return transformProject(res.data);
};

export const putProject = async (data: Project): Promise<Project> => {
  const res = await createApiCall(
    {
      path: '/v1/projects',
      method: 'PUT',
      body: transformToRequestProject(data),
    },
    {
      getMessage: {
        error: (error: any) => {
          const message =
            error.response?.data?.message || 'Failed to update project';

          if (message.toLowerCase().startsWith('duplicate')) {
            return 'A project with this name already exists for this customer.';
          }

          return message;
        },
      },
    }
  )();

  showNotification('Project successfully updated', { variant: 'success' });
  return transformProject(res.data);
};

export const fetchProjectsAPI = async (
  config: RequestConfig
): Promise<DataWithPagination<Project>> => {
  const {
    pagination = defaultMaximumPagination,
    expands = [],
    customQuickSearchColumns = ['name'],
    quickSearchValue,
  } = config;

  let path = `/v1/projects`;

  if (expands.length > 0) {
    path += `?expand=${expands.join(',')}`;
  }

  if (quickSearchValue) {
    const separator = path.includes('?') ? '&' : '?';
    path += `${separator}quickSearchColumns=${customQuickSearchColumns.join(
      ','
    )}&quickSearchValue=${quickSearchValue}`;
  }

  const res = await paginatedApiCallZeroBased(
    path,
    pagination,
    transformProject
  );
  return res;
};

export const getProjectById = async (id: number): Promise<Project> => {
  const res = await createApiCall(
    {
      path: `/v1/projects/${id}`,
      method: 'GET',
    },
    {
      getMessage: {
        error: (error: any) =>
          error.response?.data?.message || 'Failed to fetch project',
      },
    }
  )();

  return transformProject(res.data);
};

export const deleteProjectById = async (id: number): Promise<void> => {
  await createApiCall(
    {
      path: `/v1/projects/${id}`,
      method: 'DELETE',
    },
    {
      getMessage: {
        error: (error: any) =>
          error.response?.data?.message || 'Failed to delete project',
      },
    }
  )();
};

export const getProjectsByCustomerId = async (
  customerId: number
): Promise<Project[]> => {
  const res = await createApiCall(
    {
      path: `/v1/projects?customer.id=${customerId}`,
      method: 'GET',
    },
    {
      getMessage: {
        error: (error: any) =>
          error.response?.data?.message || 'Failed to fetch projects',
      },
    }
  )();

  if (Array.isArray(res.data?.results)) {
    return res.data.results.map(transformProject);
  }

  return [];
};
